"use client";

import { ChevronDown, ImageIcon } from "lucide-react";
import { useBooking } from "../context";
import { cancellationPolicy, type Cancellation } from "../checkout";

/** The class being booked, as checkout, quick-book and confirmation show it. */
export type BookingClass = {
  id: string;
  type: string;
  title: string;
  description?: string | null;
  /** Level, style… as the studio tagged the class. */
  tags?: string[] | null;
  imageUrl?: string | null;
  startAt: string;
  endAt: string;
  teacherName?: string | null;
  room?: string | null;
  isOnline?: boolean | null;
  spotsLeft: number | null;
  waitlistCount: number;
  waitlistEnabled: boolean;
  /** Free cancellation, computed by the API (older answers leave it out). */
  cancellation?: Cancellation | null;
};

/**
 * "Free cancellation until Wed 4 Nov 18:00", or "No free cancellation for
 * this class" once the deadline has passed. The API's answer wins; the local
 * rule only covers an older API that doesn't send it.
 */
export function useCancelNote(session: {
  startAt: string;
  type?: string | null;
  cancellation?: Cancellation | null;
}) {
  const { t, fmt } = useBooking();
  const policy = session.cancellation ?? cancellationPolicy(session.startAt, session.type);
  if (!policy.isFree) return t("checkout.noFreeCancel");
  return t("checkout.freeCancelUntil", { when: `${fmt.dayShort(policy.freeUntil)} ${fmt.time(policy.freeUntil)}` });
}

/** "Mon 2 Nov · 18:30 – 19:45" */
export function useClassWhen(session: Pick<BookingClass, "startAt" | "endAt">) {
  const { fmt } = useBooking();
  return `${fmt.dayShort(session.startAt)} · ${fmt.time(session.startAt)} – ${fmt.time(session.endAt)}`;
}

export function ClassHeader({ session, size = "md" }: { session: BookingClass; size?: "sm" | "md" }) {
  const { t } = useBooking();
  const when = useClassWhen(session);
  const where = session.isOnline ? t("schedule.online") : session.room;
  const thumb = size === "sm" ? "size-14" : "size-[72px]";

  return (
    <div className="flex items-center gap-3.5">
      {session.imageUrl ? (
        // Host-resolved studio image; a plain img works in both apps.
        <img src={session.imageUrl} alt="" className={`${thumb} shrink-0 rounded-[14px] object-cover`} />
      ) : (
        <span
          aria-hidden="true"
          className={`${thumb} flex shrink-0 items-center justify-center rounded-[14px] bg-studio-accent-soft text-studio-ink`}
        >
          <ImageIcon size={18} />
        </span>
      )}
      <div className="min-w-0">
        <p className={`font-display font-medium text-ink ${size === "sm" ? "text-lg" : "text-xl"}`}>
          {session.title}
        </p>
        <p className="text-[13px] leading-5 text-ink-soft">
          {when}
          {(session.teacherName || where) && <br />}
          {session.teacherName && t("schedule.withTeacher", { name: session.teacherName })}
          {session.teacherName && where && " · "}
          {where}
        </p>
      </div>
    </div>
  );
}

/** Collapsible description and level. Nothing is shown when the studio wrote neither. */
export function AboutThisClass({ session }: { session: Pick<BookingClass, "description" | "tags"> }) {
  const { t } = useBooking();
  const description = session.description?.trim();
  const level = session.tags?.filter(Boolean).join(" · ");
  if (!description && !level) return null;

  return (
    <details className="group rounded-control bg-surface-muted">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-control px-3 text-[13px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink [&::-webkit-details-marker]:hidden">
        {t("checkout.aboutClass")}
        <ChevronDown size={16} aria-hidden="true" className="transition-transform group-open:rotate-180" />
      </summary>
      <div className="flex flex-col gap-2 px-3 pb-3 text-sm text-ink-soft">
        {level && <p className="font-medium text-ink">{t("checkout.level", { level })}</p>}
        {description && <p className="whitespace-pre-line">{description}</p>}
      </div>
    </details>
  );
}
